import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import { markFirstLoginIfNeeded } from '../utils/mobileAppLinks';
import './invite-landing.css';

const API = getApiBaseUrl();

const ROLE_LABELS = {
    Coach: 'тренера',
    Student: 'ученика',
    Parent: 'родителя'
};

export default function ActivatePage() {
    const { activationToken } = useParams();
    const [searchParams] = useSearchParams();
    const inviteToken = searchParams.get('invite') || '';

    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        if (!activationToken) {
            setLoadError('Ссылка недействительна — токен не найден.');
            setLoading(false);
            return;
        }
        axios.get(`${API}/invite/activate/${encodeURIComponent(activationToken)}`)
            .then((res) => {
                setInfo(res.data);
                setLoading(false);
            })
            .catch((err) => {
                setLoadError(err.response?.data?.error === 'invite_already_used'
                    ? 'Эта ссылка уже была использована — попробуйте войти обычным способом.'
                    : 'Ссылка недействительна или истекла. Обратитесь к администратору школы за новым приглашением.');
                setLoading(false);
            });
    }, [activationToken]);

    const hasLength = password.length >= 8;
    const hasDigit = /[0-9]/.test(password);
    const hasLetter = /[a-zA-Zа-яА-ЯёЁ]/.test(password);
    const matches = confirmPassword.length > 0 && confirmPassword === password;
    const canSubmit = hasLength && hasDigit && hasLetter && matches && !submitting;

    async function handleSubmit(e) {
        e.preventDefault();
        setSubmitError('');
        if (!hasLength || !hasDigit || !hasLetter) {
            setSubmitError('Пароль должен быть минимум 8 символов и содержать букву и цифру');
            return;
        }
        if (password !== confirmPassword) {
            setSubmitError('Пароли не совпадают');
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/invite/activate`, {
                activationToken,
                password,
                inviteToken: inviteToken || undefined
            });
            const data = res.data;
            if (data.token) {
                localStorage.setItem('token', data.token);
                markFirstLoginIfNeeded();
            }
            window.location.href = data.redirectUrl || '/';
        } catch (err) {
            const code = err.response?.data?.error;
            setSubmitError(
                code === 'invalid_token' ? 'Ссылка недействительна или истекла'
                : code === 'invite_already_used' ? 'Эта ссылка уже была использована'
                : code === 'password_too_short' ? 'Пароль слишком короткий'
                : 'Не удалось установить пароль. Попробуйте ещё раз.'
            );
        }
        setSubmitting(false);
    }

    if (loading) {
        return (
            <div className="invite-landing-page">
                <div className="invite-landing-card">
                    <h1>Настройка аккаунта</h1>
                    <p className="invite-landing-sub">Загрузка...</p>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="invite-landing-page">
                <div className="invite-landing-card">
                    <h1>Настройка аккаунта</h1>
                    <p className="invite-landing-error">{loadError}</p>
                    <a className="invite-landing-btn" href="/login">Войти</a>
                </div>
            </div>
        );
    }

    const roleLabel = ROLE_LABELS[info?.expectedRole] || 'участника';

    return (
        <div className="invite-landing-page">
            <div className="invite-landing-card" style={{ textAlign: 'left' }}>
                <h1 style={{ textAlign: 'center' }}>Добро пожаловать{info?.recipientName ? `, ${info.recipientName}` : ''}!</h1>
                <p className="invite-landing-sub" style={{ textAlign: 'center' }}>
                    Вас пригласили в «{info?.schoolName || 'школу'}» как {roleLabel}. Придумайте пароль, чтобы войти в аккаунт.
                </p>
                <form onSubmit={handleSubmit}>
                    <label style={{ display: 'block', marginBottom: 14 }}>
                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: 6 }}>
                            Придумайте пароль
                        </span>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Минимум 8 символов"
                            autoFocus
                            style={inputStyle}
                        />
                    </label>
                    {password.length > 0 && (
                        <div style={{ marginBottom: 14, marginTop: -6 }}>
                            <CheckRow ok={hasLength} label="Минимум 8 символов" />
                            <CheckRow ok={hasLetter} label="Есть буква" />
                            <CheckRow ok={hasDigit} label="Есть цифра" />
                        </div>
                    )}
                    <label style={{ display: 'block', marginBottom: 8 }}>
                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: 6 }}>
                            Повторите пароль
                        </span>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Введите пароль ещё раз"
                            style={inputStyle}
                        />
                    </label>
                    {confirmPassword.length > 0 && (
                        <p style={{ fontSize: 12, fontWeight: 600, margin: '0 0 14px', color: matches ? '#4ade80' : '#f87171' }}>
                            {matches ? '✓ Пароли совпадают' : '✗ Пароли не совпадают'}
                        </p>
                    )}
                    {submitError && <p className="invite-landing-error">{submitError}</p>}
                    <button type="submit" className="invite-landing-btn" disabled={!canSubmit} style={{ border: 'none', width: '100%', cursor: canSubmit ? 'pointer' : 'not-allowed', opacity: canSubmit ? 1 : 0.6, marginTop: 6 }}>
                        {submitting ? 'Сохраняем...' : 'Установить пароль и войти'}
                    </button>
                </form>
            </div>
        </div>
    );
}

function CheckRow({ ok, label }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <span style={{ fontSize: 12, color: ok ? '#4ade80' : 'rgba(255,255,255,0.35)', width: 14, fontWeight: 700 }}>{ok ? '✓' : '○'}</span>
            <span style={{ fontSize: 12, color: ok ? '#4ade80' : 'rgba(255,255,255,0.45)' }}>{label}</span>
        </div>
    );
}

const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10,
    border: '1.5px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)',
    color: '#fff', fontSize: 14, boxSizing: 'border-box', outline: 'none'
};
